import { Component } from '@theme/component';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * "Nimm 2"-Button der Produktseite.
 *
 * Legt das Shirt dieser Seite in der gewaehlten Groesse und Farbe ein – das
 * ist Shirt 1 – und oeffnet danach das Aktions-Popup (`cart-upsell-popup`),
 * das dann schon bei "1/2" steht. Dort waehlt der Kunde nur noch sein zweites
 * Shirt und anschliessend das Schmuckstueck.
 *
 * Bewusst keine eigene Auswahl-UI: das Popup kann Shirts und Schmuck bereits
 * auswaehlen, ein zweites Fenster davor war doppelt.
 *
 * Der Zugang laeuft ueber `/cart/add.js` statt ueber das Produktformular, damit
 * die Reihenfolge feststeht: erst einlegen, dann das Popup mit frischem Stand
 * oeffnen, zuletzt Warenkorb-Symbol und Drawer benachrichtigen. Ueber das
 * Formular oeffnete das Popup sofort mit dem alten Stand ("0/2") und sprang
 * eine Sekunde spaeter um.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} button - Der Bundle-Button.
 *
 * @extends Component<Refs>
 */
class BundleAddComponent extends Component {
  requiredRefs = ['button'];

  /** Laeuft gerade ein Zugang? Schuetzt vor Doppelklicks. */
  #busy = false;

  /**
   * Die im Variantenwaehler gewaehlte Variante.
   *
   * Gelesen wird das versteckte Feld des Hauptformulars in derselben Section.
   * Ohne diese Eingrenzung koennte auf Seiten mit Quick-Add-Karten das Feld
   * eines fremden Produkts erwischt werden.
   *
   * @returns {string | null}
   */
  get #variantId() {
    const scope = this.closest('.shopify-section') ?? document;
    const input = /** @type {HTMLInputElement | null} */ (
      scope.querySelector('product-form-component input[name="id"]')
    );

    return input?.value || null;
  }

  /** @param {Event} event */
  handleClick = async (event) => {
    event.preventDefault();

    if (this.#busy) return;

    const variantId = this.#variantId;
    if (!variantId) return;

    this.#busy = true;
    this.refs.button.disabled = true;

    try {
      const cart = await this.#add(Number(variantId));
      if (!cart) return;

      const productId = this.dataset.productId ?? '';
      const popup = document.querySelector('cart-upsell-popup');
      if (popup) await customElements.whenDefined('cart-upsell-popup');

      /** @type {{ openForProduct?: (id: string) => Promise<boolean> } | null} */
      const upsell = /** @type {any} */ (popup);
      const handled = Boolean(await upsell?.openForProduct?.(productId));

      // `showDialog()` oeffnet erst im naechsten Frame. Ohne dieses Warten
      // saehe das Popup den Event unten noch geschlossen und oeffnete sich
      // ein zweites Mal.
      if (handled) await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

      // Erst jetzt melden: das Popup steht schon offen und uebernimmt den
      // Zugang, statt ein zweites Mal mit altem Stand aufzugehen. Das
      // Warenkorb-Symbol und der Drawer ziehen damit trotzdem nach.
      this.#notify(Number(variantId), cart);

      // Keine offene Kampagne mehr (z. B. Schmuck schon im Warenkorb): der
      // Klick braucht trotzdem eine sichtbare Antwort.
      if (!handled) this.#openCartDrawer();
    } finally {
      this.#busy = false;
      this.refs.button.disabled = false;
    }
  };

  /**
   * Legt ein Stueck ein und liefert den neuen Warenkorb – `null` bei Fehler.
   *
   * @param {number} id
   * @returns {Promise<any | null>}
   */
  async #add(id) {
    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id, quantity: 1 }] }),
      }).then((result) => result.json());

      if (response.status) throw new Error(response.description || response.message || 'Add to cart failed');

      return await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((result) => result.json());
    } catch (error) {
      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Shirt konnte nicht hinzugefuegt werden',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      return null;
    }
  }

  /**
   * Derselbe Event wie aus `product-form.js`, damit Warenkorb-Symbol und
   * Drawer den neuen Stand uebernehmen.
   *
   * @param {number} id
   * @param {any} cart
   */
  #notify(id, cart) {
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(id), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'bundle-add-component',
        sourceId: this.id?.toString() ?? '',
        itemCount: 1,
        productId: this.dataset.productId,
        didError: false,
      },
    });
  }

  /**
   * Oeffnet den Warenkorb-Drawer, sofern das Theme einen hat.
   *
   * @returns {Promise<void>}
   */
  async #openCartDrawer() {
    const drawer = document.getElementById('cart-drawer');
    if (!drawer) return;

    await customElements.whenDefined('theme-drawer');

    /** @type {{ open?: () => void }} */ (drawer).open?.();
  }
}

if (!customElements.get('bundle-add-component')) {
  customElements.define('bundle-add-component', BundleAddComponent);
}
